import {createTheme, useTheme} from "@mui/material";
import {useMemo} from "react";
import {blue, green, grey, orange, purple} from "@mui/material/colors";
import {merge} from "lodash";

const useCustomTheme = (values) => {
  const theme = useTheme();
  const tertiary = {
    main: '#a0a0a8'
  };
  const primaryText = "#e0e0e0";

  return useMemo(() => createTheme(merge({
    palette: {
      mode: 'dark',
      primary: {
        main: '#00b0ff',
      },
      secondary: {
        main: '#9ca3af',
      },
      muted: {
        main: grey[600],
      },
      tertiary,
      depurple: {
        main: purple[500],
        light: purple[300],
        dark: purple[700],
        contrastText: '#fff',
      },
      inverted: {
        main: '#1a1a1a',
        light: '#2a2a2a',
        dark: '#0a0a0a',
        contrastText: '#FFFFFF',
      },
      text: {
        primary: primaryText,
        secondary: tertiary.main,
        muted: grey[400],
      },
      background: {
        default: '#121212',
        paper: '#1e1e1e',
      },
      badgeBlue: {
        main: blue[500],
        light: blue[300],
        dark: blue[700],
        contrastText: blue[50],
      },
      badgeGreen: {
        main: green[500],
        light: green[300],
        dark: green[700],
        contrastText: green[50],
      },
      badgeOrange: {
        main: orange[500],
        light: orange[300],
        dark: orange[700],
        contrastText: orange[50],
      },
      pendingChanges: {
        main: '#FACC15',
        light: '#FDE047',
        dark: '#EAB308',
        contrastText: '#1a1a1a',
      },
      navigation: {
        main: '#1a2332',
      }
    },
    typography: {
      fontSize: 14,
      fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
      h1: {
        fontSize: '1.375rem',
        fontWeight: '700',
        lineHeight: '1.375',
      },
      h2: {
        fontSize: '1.25rem',
        fontWeight: '700',
      },
      h3: {
        fontSize: '1.125rem',
        fontWeight: '700',
      },
      h4: {
        fontSize: '1rem',
        fontWeight: '700',
      },
      h5: {
        fontSize: '1rem',
        fontWeight: '700',
      },
      h6: {
        fontSize: '1rem',
        fontWeight: '600',
      },
      subtitle1: {
        fontSize: '0.75rem',
        fontWeight: '500',
        color: tertiary.main,
      },
      body1: {
        fontSize: '0.875rem',
      },
      body2: {
        fontSize: '0.825rem',
        color: tertiary.main,
      },
      caption: {
        fontSize: '0.825rem',
        fontWeight: '700',
        color: tertiary.main,
      }
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            fontSize: "1rem",
            textTransform: 'none',
            boxShadow: 'none',
            "&:hover": {
              boxShadow: 'none',
            },
            borderRadius: "4px",
          },
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            border: "1px solid",
            borderColor: grey[700],
            borderRadius: "8px",
            boxShadow: "none",
            backgroundColor: '#1e1e1e',
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: "4px",
          }
        }
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            paddingTop: `${theme.spacing(1)} !important`,
            paddingBottom: theme.spacing(1),
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: theme.spacing(2),
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            "& th": {
              fontSize: '0.925rem',
              fontWeight: 'bold',
              color: tertiary.main,
            },
          }
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            color: primaryText,
            fontSize: '0.875rem',
            "& a": {
              fontSize: '0.875rem',
            },
            "& button.MuiButton-text ": {
              color: primaryText,
              fontSize: '0.875rem',
            },
          }
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            fontSize: "1rem",
            "&.Mui-disabled": {
              backgroundColor: grey[900],
              "& .MuiOutlinedInput-input:disabled": {
                color: grey[500],
                "-webkit-text-fill-color": grey[500],
              }
            },
          }
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: grey[400],
            '&.Mui-disabled': {
              color: grey[500],
            },
          },
        },
      },
      MuiListSubheader: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
            fontSize: '0.75rem',
            lineHeight: 1.5,
            paddingTop: 4,
            paddingBottom: 4,
          },
        },
      },
    }
  }, values)), [theme]);
}

export default useCustomTheme;
